
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const financialData = {
    balance: 12500,
    income: 8500,
    expenses: 6200,
    savings: 2300
  };

  const recentTransactions = [
    {
      id: 1,
      title: 'راتب شهر يناير',
      amount: 8500,
      type: 'income',
      date: '2024-01-01',
      icon: 'ri-briefcase-line',
      color: 'bg-green-100 text-green-600'
    },
    {
      id: 2,
      title: 'فاتورة الكهرباء',
      amount: -450,
      type: 'expense',
      date: '2024-01-15',
      icon: 'ri-flashlight-line',
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      id: 3,
      title: 'تسوق البقالة',
      amount: -320,
      type: 'expense',
      date: '2024-01-14',
      icon: 'ri-shopping-cart-line',
      color: 'bg-blue-100 text-blue-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="fixed top-0 w-full bg-white shadow-sm z-50">
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">مرحباً بك</h1>
            <p className="text-gray-500 text-sm">إدارة أموالك بذكاء</p>
          </div>
          <button className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100">
            <i className="ri-notification-line text-xl text-gray-700"></i>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="pt-20 pb-24 px-6">
        {/* Balance Card */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-blue-100 text-sm">الرصيد الحالي</p>
              <p className="text-3xl font-bold">{financialData.balance.toLocaleString()} ريال</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-wallet-line text-2xl"></i>
            </div>
          </div>
          
          {/* Period Selector */}
          <div className="flex bg-white/20 rounded-full p-1">
            <button 
              onClick={() => setSelectedPeriod('week')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                selectedPeriod === 'week' ? 'bg-white text-blue-600' : 'text-white'
              }`}
            >
              أسبوع
            </button>
            <button 
              onClick={() => setSelectedPeriod('month')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                selectedPeriod === 'month' ? 'bg-white text-blue-600' : 'text-white'
              }`}
            >
              شهر
            </button>
            <button 
              onClick={() => setSelectedPeriod('year')}
              className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all ${
                selectedPeriod === 'year' ? 'bg-white text-blue-600' : 'text-white'
              }`}
            >
              سنة
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-3">
              <i className="ri-arrow-up-line text-lg text-green-600"></i>
            </div>
            <p className="text-gray-500 text-xs mb-1">الدخل</p>
            <p className="text-lg font-bold text-gray-800">{financialData.income.toLocaleString()}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mb-3">
              <i className="ri-arrow-down-line text-lg text-red-600"></i>
            </div>
            <p className="text-gray-500 text-xs mb-1">المصروفات</p>
            <p className="text-lg font-bold text-gray-800">{financialData.expenses.toLocaleString()}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-3">
              <i className="ri-piggy-bank-line text-lg text-blue-600"></i>
            </div>
            <p className="text-gray-500 text-xs mb-1">الادخار</p>
            <p className="text-lg font-bold text-gray-800">{financialData.savings.toLocaleString()}</p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button 
            onClick={() => navigate('/add-income')}
            className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-6 text-white transform transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3">
              <i className="ri-add-line text-2xl"></i>
            </div>
            <h3 className="font-semibold mb-1">إضافة دخل</h3>
            <p className="text-green-100 text-sm">سجل دخل جديد</p>
          </button>
          
          <button 
            onClick={() => navigate('/add-expense')}
            className="bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl p-6 text-white transform transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-3">
              <i className="ri-subtract-line text-2xl"></i>
            </div>
            <h3 className="font-semibold mb-1">إضافة مصروف</h3>
            <p className="text-red-100 text-sm">سجل مصروف جديد</p>
          </button>
        </div>

        {/* Navigation Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button 
            onClick={() => navigate('/expenses')}
            className="bg-white rounded-2xl p-6 shadow-sm transform transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-3">
              <i className="ri-arrow-down-line text-2xl text-red-600"></i>
            </div>
            <h3 className="font-semibold text-gray-800 mb-1">المصروفات</h3>
            <p className="text-gray-500 text-sm">عرض جميع المصروفات</p>
          </button>
          
          <button 
            onClick={() => navigate('/reports')}
            className="bg-white rounded-2xl p-6 shadow-sm transform transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
              <i className="ri-bar-chart-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="font-semibold text-gray-800 mb-1">التقارير</h3>
            <p className="text-gray-500 text-sm">تحليل مالي مفصل</p>
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 mb-6">
          <button 
            onClick={() => navigate('/financial-alert')}
            className="bg-white rounded-2xl p-6 shadow-sm transform transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <div className="flex items-center space-x-4 space-x-reverse">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <i className="ri-notification-line text-2xl text-orange-600"></i>
              </div>
              <div className="flex-1 text-right">
                <h3 className="font-semibold text-gray-800 mb-1">التنبيهات المالية</h3>
                <p className="text-gray-500 text-sm">إدارة التنبيهات والإعدادات</p>
              </div>
              <i className="ri-arrow-left-line text-gray-400"></i>
            </div>
          </button>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">المعاملات الأخيرة</h3>
            <button 
              onClick={() => navigate('/expenses')}
              className="text-blue-600 text-sm font-medium"
            >
              عرض الكل
            </button>
          </div>
          
          <div className="space-y-3">
            {recentTransactions.map((transaction) => (
              <div key={transaction.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-3 space-x-reverse">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${transaction.color}`}>
                    <i className={`${transaction.icon} text-lg`}></i>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{transaction.title}</p>
                    <p className="text-gray-500 text-sm">{transaction.date}</p>
                  </div>
                </div>
                <p className={`font-bold ${transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {transaction.amount > 0 ? '+' : ''}{transaction.amount} ريال
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 w-full bg-white border-t border-gray-200">
        <div className="grid grid-cols-4 py-2">
          <button 
            onClick={() => navigate('/dashboard')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-home-line text-blue-600 text-lg"></i>
            </div>
            <span className="text-xs text-blue-600 font-medium">الرئيسية</span>
          </button>
          <button 
            onClick={() => navigate('/expenses')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-arrow-down-line text-gray-400 text-lg"></i>
            </div>
            <span className="text-xs text-gray-400">المصروفات</span>
          </button>
          <button 
            onClick={() => navigate('/reports')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-bar-chart-line text-gray-400 text-lg"></i>
            </div>
            <span className="text-xs text-gray-400">التقارير</span>
          </button>
          <button 
            onClick={() => navigate('/financial-alert')}
            className="flex flex-col items-center justify-center py-2"
          >
            <div className="w-6 h-6 flex items-center justify-center mb-1">
              <i className="ri-notification-line text-gray-400 text-lg"></i>
            </div>
            <span className="text-xs text-gray-400">التنبيهات</span>
          </button>
        </div>
      </div>
    </div>
  );
}
